import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

// Note: These hooks don't match the loan-scoped factory pattern because they use
// securityId, global paths, or custom query param structures. Keeping them manual.

// ── Perfection steps by security ─────────────────────────

export function usePerfectionSteps(securityId: string) {
  return useQuery({
    queryKey: ['perfection-steps', securityId],
    queryFn: async () => {
      const { data } = await api.get(`/securities/${securityId}/perfection-steps`);
      return data;
    },
    enabled: !!securityId,
  });
}

// ── Add perfection step (manual - global resource) ───────

export function useAddPerfectionStep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ securityId, ...data }: any) => {
      const { data: result } = await api.post(`/securities/${securityId}/perfection-steps`, data);
      return result;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['perfection-steps'] });
      qc.invalidateQueries({ queryKey: ['perfection-summary'] });
      qc.invalidateQueries({ queryKey: ['outstanding-perfection'] });
      if (variables?.securityId) {
        qc.invalidateQueries({ queryKey: ['perfection-steps', variables.securityId] });
      }
    },
  });
}

// ── Update perfection step (manual - global resource) ────

export function useUpdatePerfectionStep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: any) => {
      const { data: result } = await api.patch(`/securities/perfection-steps/${id}`, data);
      return result;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['perfection-steps'] });
      qc.invalidateQueries({ queryKey: ['perfection-summary'] });
      qc.invalidateQueries({ queryKey: ['outstanding-perfection'] });
    },
  });
}

// ── Complete perfection step (manual - custom action) ────

export function useCompletePerfectionStep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: any) => {
      const { data: result } = await api.post(`/securities/perfection-steps/${id}/complete`, data);
      return result;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['perfection-steps'] });
      qc.invalidateQueries({ queryKey: ['perfection-summary'] });
      qc.invalidateQueries({ queryKey: ['outstanding-perfection'] });
    },
  });
}

// ── Outstanding perfection (manual - optional loanId param) ──

export function useOutstandingPerfection(loanId?: string) {
  return useQuery({
    queryKey: ['outstanding-perfection', loanId],
    queryFn: async () => {
      const params = loanId ? `?loanId=${loanId}` : '';
      const { data } = await api.get(`/securities/perfection/outstanding${params}`);
      return data;
    },
  });
}

// ── Perfection summary (manual - summary query) ─────────

export function usePerfectionSummary(loanId: string) {
  return useQuery({
    queryKey: ['perfection-summary', loanId],
    queryFn: async () => {
      const { data } = await api.get(`/securities/perfection/summary/${loanId}`);
      return data;
    },
    enabled: !!loanId,
  });
}
